import { toJS } from 'mobx'
import { difference } from 'lodash'

import ActionStore from 'stores/action/ActionStore'
import Action from 'stores/action/Action'
import RecordStore from 'stores/record/RecordStore'

import createError from 'utils/createError'
import getRecordId from 'utils/getRecordId'
import stringify from 'utils/stringify'

type IOperationResult = {
  actions: Action[]
  requests: string[]
}
type IOperation = (actions: Action[]) => IOperationResult

/**
 * Generates Prisma Client queries from actions
 *
 * This relies on actions being consolidated correctly. Will warn if some actions are not processed
 */
const photonRequestsFromActions = (actionIds: string[]): string[] => {
  const actions = actionIds.map(aId => ActionStore.get(aId))

  // Each one of `_create`, `_update` & `_delete` will mutate its own arguments, removing actions it has handled, so beware

  // The order we're processing actions is important.
  // First process `delete` actions, so that records that are deleted at any point never make it to the final requests
  // Then, handle `create` actions, so that records that are created (and updated) make it in the same request. Without being in the same request, their links would break (since IDs would change after creation)
  // Then, process `update` actions. The only update actions left would be ones on already committed records
  const operations = [_delete, _create, _update]

  const operationResult: IOperationResult = operations.reduce(
    (results, operation) => {
      const operationResult: IOperationResult = operation(results.actions)
      results.actions = operationResult.actions
      results.requests.push(...operationResult.requests)
      return results
    },
    {
      actions,
      requests: [],
    } as IOperationResult
  )

  if (operationResult.actions.length > 0) {
    console.warn(
      'All actions were not processed. Pending actions: ',
      JSON.stringify(toJS(operationResult.actions), null, 2)
    )
  }

  return operationResult.requests
}

const _delete: IOperation = actions => {
  const requests: string[] = []
  const deleteActions = actions.filter(a => a.type === 'delete')

  deleteActions.forEach(action => {
    if (!action.record) {
      throw createError(
        'photonRequestsFromActions._delete',
        'Unrecognized Record in Action',
        { action: action.serialize() }
      )
    }

    const model = action.record.model
    const requestPayload = {
      where: _getRequestWhereArgument(action),
    } // Argument that will go inside the photon function call after being stringified

    requests.push(
      [
        `prisma.${model.photonNamespace}.delete(`,
        `${stringify(requestPayload)}`,
        `)`,
      ].join('\n')
    )

    actions = actions.filter(
      a => action.id !== a.id && action.recordId !== a.recordId // Remove the action you just handled + any other actions on this record, because deleting this record means those will have no effect
    )
  })

  return { actions, requests }
}

const _create: IOperation = actions => {
  const requests: string[] = []
  const createActions = actions.filter(a => a.type === 'create')

  createActions.forEach(action => {
    if (!action.record) {
      throw createError(
        'photonRequestsFromActions._create',
        'Unrecognized Record in Action',
        { action: action.serialize() }
      )
    }

    const model = action.record.model
    const requestPayload = {
      data: {},
      select: _getRequestSelectArgument(action),
    } // Argument that will go inside the photon function call after being stringified

    const relatedUpdateAction = actions.find(
      a =>
        a.type === 'update' &&
        a.sessionId === action.sessionId &&
        a.recordId === action.recordId
    ) // Find other update actions on this record+session so you can batch it in this create request.

    if (relatedUpdateAction) {
      // If such an action exists, add its data and merge its select
      requestPayload.data = _getCreateRequestDataArgument(relatedUpdateAction)
      requestPayload.select = {
        ...requestPayload.select,
        ..._getRequestSelectArgument(relatedUpdateAction),
      }
    }

    requests.push(
      [
        `prisma.${model.photonNamespace}.create(`,
        `${stringify(requestPayload)}`,
        `)`,
      ].join('\n')
    )

    // Remove the `create` and the related `update` actions, they are handled
    actions = actions.filter(
      a => a.id !== relatedUpdateAction?.id && action.id !== a.id
    )
  })

  return { actions, requests }
}

const _update: IOperation = actions => {
  const requests: string[] = []
  const updateActions = actions.filter(a => a.type === 'update')

  updateActions.forEach(action => {
    if (!action.record) {
      throw createError(
        'photonRequestsFromActions._update',
        'Unrecognized Record in Action',
        { action: action.serialize() }
      )
    }

    const model = action.record.model
    const requestPayload = {
      where: _getRequestWhereArgument(action),
      data: _getUpdateRequestDataArgument(action),
      select: _getRequestSelectArgument(action),
    } // Argument that will go inside the photon function call after being stringified

    requests.push(
      [
        `prisma.${model.photonNamespace}.update(`,
        `${stringify(requestPayload)}`,
        `)`,
      ].join('\n')
    )

    // Remove the `update` action, it is handled
    actions = actions.filter(a => action.id !== a.id)
  })

  return { actions, requests }
}

/**
 * Converts a single action to a photon request's `select` argument
 */
const _getRequestSelectArgument = (action: Action) => {
  if (!action.record) {
    throw createError(
      'photonRequestsFromActions._getRequestSelectArgument',
      'Unrecognized Record in Action',
      { action: action.serialize() }
    )
  }

  // Always fetch fields that will be used to uniquely identify this record
  const uniqueIdentifiers = action.record.model.uniqueIdentifiers
  const select = uniqueIdentifiers.reduce((selectAcc: any, field) => {
    selectAcc[field.name] = true
    return selectAcc
  }, {})

  if (action.type === 'create' || action.type === 'delete') {
    return select
  }

  // And add to it fields whose values were changed
  return Object.keys(action.value).reduce((select: any, fieldName) => {
    select[fieldName] = true
    return select
  }, select)
}

/**
 * Converts a single action to a photon request's `where` argument
 */
const _getRequestWhereArgument = (action: Action) => {
  if (!action.record) {
    throw createError(
      'photonRequestsFromActions._getRequestWhereArgument',
      'Unrecognized Record in Action',
      { action: action.serialize() }
    )
  }

  // To calculate the `where` argument, we need to uniquely identify this record
  const uniqueIdentifiers = action.record.model.uniqueIdentifiers
  const whereArgumentName = uniqueIdentifiers.map(f => f.name).join('_') // like id_name etc. (needed for compound IDs)
  const whereArgumentValue = uniqueIdentifiers.reduce((acc: any, field) => {
    acc[field.name] = action.record!.valueInDB[field.name] // `!` is valid because we've already checked that the record exists
    // We're using valueInDB here so that if the user changed the ID field itself, the `where` argument still uses the unchanged value to identify the record. Otherwise, the query will be incorrect.
    return acc
  }, {})

  if (uniqueIdentifiers.length === 1) {
    // For models with simple IDs / uniques (can be `where: { id: 0 }`)
    return whereArgumentValue
  } else {
    // For models with compound IDs / uniques (needs to be `where: { id_name: { id: 0. name: "xx" }`)
    return {
      [whereArgumentName]: whereArgumentValue,
    }
  }
}

/**
 * Converts a single action to a photon.<model>.update request's `data` argument
 */
const _getUpdateRequestDataArgument = (action: Action) => {
  if (!action.record) {
    throw createError(
      'photonRequestsFromActions._getUpdateRequestDataArgument',
      'Unrecognized Record in Action',
      { action: action.serialize() }
    )
  }

  const model = action.record.model

  return Object.keys(action.value).reduce((data: any, fieldName) => {
    const field = model.getFieldByName(fieldName)
    const fieldValue = action.value[fieldName]

    if (!field) {
      throw createError(
        'photonRequestsFromActions._getUpdateRequestDataArgument',
        'Malformed field in action',
        { action: action.serialize() }
      )
    }

    if (field.isReadOnly) {
      // Readonly fields cannot be changed, so skip over them
      return data
    }

    if (fieldValue === undefined) {
      // Fields without a value do not mean anything.
      // The value will be `null` if it is manually cleared
      return data
    }

    if ((field.isScalar && field.isList) || (field.isEnum && field.isList)) {
      // Scalar & Enum lists
      data[fieldName] = {
        set: fieldValue,
      }
    } else if (field.isScalar || field.isEnum) {
      // Scalars & Enums
      data[fieldName] = fieldValue
    } else if (field.isList && field.isRelation) {
      // Relation Lists
      // For relation lists, the action's value is the `Array<value>` of the records you are trying to connect to.
      if (!field.typeAsModel) {
        throw createError(
          'photonRequestsFromActions._getUpdateRequestDataArgument',
          'Invalid field type',
          { field: field.serialize() }
        )
      }

      const originallyConnectedRecordIds = (
        action.record!.valueInDB[fieldName] || []
      ).map((v: any) => getRecordId(field.typeAsModel!.id, v))
      const newConnectedRecordIds = fieldValue.map((v: any) =>
        getRecordId(field.typeAsModel!.id, v)
      )
      const toConnect: string[] = difference(
        newConnectedRecordIds,
        originallyConnectedRecordIds
      )
      const toDisconnect: string[] = difference(
        originallyConnectedRecordIds,
        newConnectedRecordIds
      )

      data[fieldName] = {
        // For record IDs that are supposed to be connected
        connect: toConnect.map((rId, index) => {
          // If the record we are trying to connect to does not exist, create it synthetically.
          // This may happen when you type in a record id to connect to in the table's cell (and the relation it is part of gets updated)
          // We cannot know if this record actually exists in the DB, so we're creating this record as a placeholder. If it does not actually exist in the DB, Prisma Client will throw (which is fine)
          const record =
            RecordStore.get(rId) ||
            RecordStore.add({
              id: rId,
              modelId: field.typeAsModel!.id, // ! is fine because we've checked for its existence above
              value: fieldValue[index],
            })

          // Connect using fields that uniquely identify this record
          const uniqueIdentifiers = record.model.uniqueIdentifiers

          const setArgumentName = uniqueIdentifiers.map(f => f.name).join('_') // like id_name etc. (needed for compound IDs)
          const setArgumentValue = uniqueIdentifiers.reduce(
            (set: any, field) => {
              set[field.name] = record.valueInDB[field.name]
              return set
            },
            {}
          )

          if (uniqueIdentifiers.length === 1) {
            // For relations that connect to simple models (can be `connect: { id: 0 }`)
            return setArgumentValue
          } else {
            // For relations that connect to models with compound IDs (needs to be `connect: { id_name: { id: 0. name: "xx" }`)
            return {
              [setArgumentName]: setArgumentValue,
            }
          }
        }),
        // For record IDs that are supposed to be connected
        disconnect: toDisconnect.map((rId, index) => {
          const record = RecordStore.get(rId)
          if (!record) {
            throw createError(
              'photonRequestsFromActions._getUpdateRequestDataArgument',
              'Attempting to disconnect a non-existent record',
              { action: action.serialize(), index }
            )
          }

          // Disconnect using fields that uniquely identify this record
          const uniqueIdentifiers = record.model.uniqueIdentifiers

          const setArgumentName = uniqueIdentifiers.map(f => f.name).join('_') // like id_name etc. (needed for compound IDs)
          const setArgumentValue = uniqueIdentifiers.reduce(
            (set: any, field) => {
              set[field.name] = record.valueInDB[field.name]
              return set
            },
            {}
          )

          if (uniqueIdentifiers.length === 1) {
            // For relations that connect to simple models (can be `connect: { id: 0 }`)
            return setArgumentValue
          } else {
            // For relations that connect to models with compound IDs (needs to be `connect: { id_name: { id: 0. name: "xx" }`)
            return {
              [setArgumentName]: setArgumentValue,
            }
          }
        }),
      }
    } else if (field.isRelation) {
      // Relations
      if (fieldValue === null) {
        data[fieldName] = {
          disconnect: true,
        }
      } else {
        // For relations, the action's value is the `value` of the record you are trying to connect to.
        if (!field.typeAsModel) {
          throw createError(
            'photonRequestsFromActions._getUpdateRequestDataArgument',
            'Invalid field type',
            { field: field.serialize() }
          )
        }

        const connectedRecordId = getRecordId(field.typeAsModel.id, fieldValue)
        // If the record we are trying to connect to does not exist, create it synthetically.
        // This may happen when you type in a record id to connect to in the table's cell (and the relation it is part of gets updated)
        // We cannot know if this record actually exists in the DB, so we're creating this record as a placeholder. If it does not actually exist in the DB, Prisma Client will throw (which is fine)
        const connectedRecord =
          RecordStore.get(connectedRecordId) ||
          RecordStore.add({
            id: connectedRecordId,
            modelId: field.typeAsModel.id,
            value: fieldValue,
          })

        // Connect using fields that uniquely identify this record
        const uniqueIdentifiers = connectedRecord.model.uniqueIdentifiers

        const connectArgumentName = uniqueIdentifiers.map(f => f.name).join('_') // like id_name etc. (needed for compound IDs)
        const connectArgumentValue = uniqueIdentifiers.reduce(
          (connect: any, field) => {
            connect[field.name] = connectedRecord.valueInDB[field.name]
            return connect
          },
          {}
        )

        if (uniqueIdentifiers.length === 1) {
          // For relations that connect to simple models (can be `connect: { id: 0 }`)
          data[fieldName] = {
            connect: connectArgumentValue,
          }
        } else {
          // For relations that connect to models with compound IDs (needs to be `connect: { id_name: { id: 0. name: "xx" }`)
          data[fieldName] = {
            connect: {
              [connectArgumentName]: connectArgumentValue,
            },
          }
        }
      }
    }

    return data
  }, {})
}

/**
 * Converts a single action to a photon.<model>.create request's `data` argument.
 * Almost the same thing as photon.<model>.update, with a few adjustments
 */
const _getCreateRequestDataArgument = (action: Action) => {
  // First, get the `data` argument as if this was an `update` request
  const data = _getUpdateRequestDataArgument(action)

  // For create requests, we need to make some changes to the `data` argument:
  // 1. Remove all `<relation>.disconnect`s
  // 2. Remove all `<relationList>.disconnect`

  Object.keys(data).forEach(fieldName => {
    if (!action.record) {
      throw createError(
        'photonRequestsFromActions._getCreateRequestDataArgument',
        'Unrecognized Record in Action',
        { action: action.serialize() }
      )
    }

    const field = action.record.model.getFieldByName(fieldName)
    if (!field) {
      throw createError(
        'photonRequestsFromActions._getUpdateRequestDataArgument',
        'Malformed field in action',
        { action: action.serialize() }
      )
    }

    // Remove all `<relation>.disconnect`s
    if (field.isRelation && !field.isList && data[fieldName].disconnect) {
      delete data[fieldName]
    }

    // Remove all `<relationList>.disconnect`s
    if (field.isRelation && field.isList && data[fieldName].disconnect) {
      delete data[fieldName].disconnect
    }
  })

  return data
}

export default photonRequestsFromActions
